import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { apiFetch, assetUrl } from "../utils/api";

export function formatEventTime(start, end) {
  if (start && end) return `${start} 至 ${end}`;
  if (start) return start;
  if (end) return `至 ${end}`;
  return "时间待定";
}

const CONTENT_IMAGE_RE = /\[\[IMG:([^\]]+)\]\]|!\[([^\]]*)\]\(([^)]+)\)/g;

export function EventRichContent({ content }) {
  if (!content) return null;

  const nodes = [];
  let lastIndex = 0;
  const matcher = new RegExp(CONTENT_IMAGE_RE.source, "g");
  let match;

  while ((match = matcher.exec(content))) {
    if (match.index > lastIndex) {
      nodes.push(
        <p key={`t-${lastIndex}`} className="whitespace-pre-line leading-[2.05] tracking-wide">
          {content.slice(lastIndex, match.index)}
        </p>
      );
    }
    const url = match[1] || match[3];
    if (url) {
      nodes.push(
        <img
          key={`i-${match.index}`}
          src={assetUrl(url)}
          alt={match[2] || "活动图片"}
          className="w-full rounded-2xl my-6 object-cover shadow-sm"
        />
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    nodes.push(
      <p key={`t-${lastIndex}`} className="whitespace-pre-line leading-[2.05] tracking-wide">
        {content.slice(lastIndex)}
      </p>
    );
  }

  return (
    <div className="text-gray-700 text-[16px] md:text-[17px] leading-[2.05] tracking-wide space-y-6">
      {nodes}
    </div>
  );
}

export function EventStatusBadge({ status, label }) {
  const styles = {
    upcoming: "bg-pink-50 text-pink-600",
    ongoing: "bg-green-50 text-green-600",
    ended: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs ${styles[status] || styles.upcoming}`}>
      {label || "即将开始"}
    </span>
  );
}

export function OfflineEventCard({ item }) {
  return (
    <Link
      to={`/events/${item.id}`}
      className="bg-white rounded-2xl shadow hover:-translate-y-1 transition overflow-hidden border border-transparent hover:border-pink-100 block"
    >
      {item.cover_url ? (
        <img
          src={assetUrl(item.cover_url)}
          alt={item.title}
          className="w-full h-40 object-cover"
        />
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-rose-100 to-amber-100 flex items-center justify-center text-5xl">
          🎪
        </div>
      )}
      <div className="p-5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-pink-500 font-medium">{item.category_label}</span>
          <EventStatusBadge status={item.status} label={item.status_label} />
        </div>
        <h4 className="font-bold text-lg text-gray-800 line-clamp-1">{item.title}</h4>
        <p className="text-sm text-gray-500 line-clamp-2">
          {item.summary || "点击查看活动详情与报名方式"}
        </p>
        <p className="text-xs text-gray-400">
          {item.city || "城市待定"}
          {item.venue ? ` · ${item.venue}` : ""}
        </p>
        <p className="text-xs text-gray-400">{formatEventTime(item.start_at, item.end_at)}</p>
        {item.fee_label ? (
          <p className="text-sm text-rose-500 font-medium">{item.fee_label}</p>
        ) : null}
      </div>
    </Link>
  );
}

export default function EventsPage() {
  const [categories, setCategories] = useState({});
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "线下活动_相亲联谊沙龙 - AI婚恋平台",
    metaDescription:
      "平台线下活动发布：相亲联谊、情感沙龙、成长工作坊。查看时间地点与报名方式。",
    ogDescription: "线下相亲联谊与情感沙龙活动发布。",
    keywords: "线下活动,相亲会,联谊,情感沙龙,婚恋活动",
    path: "/events",
  });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", "50");
    if (category) params.set("category", category);
    apiFetch(`/api/public/events?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        setCategories(data.categories || {});
        setItems(data.items || []);
      })
      .catch(() => {
        setCategories({});
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-10">
        <div className="max-w-6xl mx-auto space-y-8">
          <header className="text-center space-y-3">
            <p className="text-sm tracking-widest text-pink-500 font-medium">交友活动</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">线下活动</h1>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              相亲联谊、情感沙龙、成长工作坊。报名后见面交流，把线上缘分带到线下。
            </p>
          </header>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setCategory("")}
              className={`px-4 py-2 rounded-full text-sm border ${
                category === ""
                  ? "bg-pink-50 border-pink-400 text-pink-600"
                  : "bg-white border-gray-200 text-gray-500"
              }`}
            >
              全部
            </button>
            {Object.entries(categories).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`px-4 py-2 rounded-full text-sm border ${
                  category === key
                    ? "bg-pink-50 border-pink-400 text-pink-600"
                    : "bg-white border-gray-200 text-gray-500"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="bg-white p-10 rounded-2xl shadow text-center text-gray-400">
              正在加载活动...
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white p-10 rounded-2xl shadow text-center text-gray-400">
              暂无线下活动，请稍后再来看
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <OfflineEventCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}